import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import {
  ActionIcon,
  Button,
  Checkbox,
  FileButton,
  Group,
  Input,
  MultiSelect,
  Paper,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconFile, IconFileUpload, IconPaperclip, IconPhoto, IconX } from '@tabler/icons-react';
import type { FieldSchema } from '../types/schema';
import AddressField from './AddressField';
import { evaluateShowIf } from '../utils/showIf';
import { renderMarkdownLabel } from '../utils/markdownLabel';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')) {
    return IconPhoto;
  }
  return IconFile;
}

interface FormFieldProps {
  field: FieldSchema;
  onFilesSelected?: (fieldId: string, files: File[]) => File[];
  variant?: 'default' | 'voucherDocument';
}

function getRequiredMessage(field: FieldSchema): string {
  const label = field.label ?? '';
  switch (field.type) {
    case 'radio':
    case 'select':
      return 'Please select an option';
    case 'multiselect':
      return 'Please select at least one option';
    case 'address':
      return 'Required';
    default:
      return !label || label.length > 60 ? 'This field is required' : `${label} is required`;
  }
}

function dynamicFallback(rules: Array<{ prefix: string; maxLength: number }>): number {
  return Math.max(...rules.map((r) => r.maxLength));
}

function getEffectiveMaxLength(
  value: string | undefined,
  staticMax: number | undefined,
  dynamicRules?: Array<{ prefix: string; maxLength: number }>,
): number {
  const fallback = staticMax ?? (dynamicRules ? dynamicFallback(dynamicRules) : Infinity);
  if (!dynamicRules || !value) return fallback;
  const upper = value.toUpperCase();
  for (const rule of dynamicRules) {
    if (upper.startsWith(rule.prefix.toUpperCase())) {
      return rule.maxLength;
    }
  }
  return fallback;
}

function CharCounter({
  fieldId,
  maxLength,
  dynamicMaxLength,
}: {
  fieldId: string;
  maxLength: number;
  dynamicMaxLength?: Array<{ prefix: string; maxLength: number }>;
}) {
  const value = useWatch({ name: fieldId }) as string | undefined;
  const effective = getEffectiveMaxLength(value, maxLength, dynamicMaxLength);
  const len = value?.length ?? 0;
  return (
    <Text size="xs" c="dimmed" ta="right" mt={4} style={{ marginBottom: -6 }}>
      {len}/{effective}
    </Text>
  );
}

function DynamicMaxLengthInput({
  field,
  label,
  inputType,
  errorMsg,
  registration,
  setValue,
}: {
  field: FieldSchema;
  label: string;
  inputType: string;
  errorMsg: string;
  registration: ReturnType<ReturnType<typeof useFormContext>['register']>;
  setValue: ReturnType<typeof useFormContext>['setValue'];
}) {
  const value = useWatch({ name: field.id }) as string | undefined;
  const effective = getEffectiveMaxLength(
    value,
    field.validation?.maxLength,
    field.validation?.dynamicMaxLength,
  );

  const autoPrefixRules = field.validation?.autoPrefix;

  return (
    <TextInput
      label={renderMarkdownLabel(label)}
      description={field.helpText ? renderMarkdownLabel(field.helpText) : undefined}
      placeholder={field.placeholder}
      required={field.required}
      type={inputType}
      error={errorMsg || undefined}
      size="sm"
      radius="md"
      maxLength={effective}
      {...registration}
      onChange={(e) => {
        registration.onChange(e);
        const inputType = (e.nativeEvent as InputEvent).inputType;
        if (autoPrefixRules && inputType === 'insertText') {
          const v = e.target.value;
          for (const rule of autoPrefixRules) {
            if (v.toUpperCase() === rule.trigger.toUpperCase() && !v.endsWith(rule.insert)) {
              setValue(field.id, v + rule.insert, { shouldValidate: false });
              break;
            }
          }
        }
      }}
      onBlur={(e) => {
        registration.onBlur(e);
        const trimmed = e.target.value.trim();
        if (trimmed !== e.target.value) {
          // Re-validate only a field that already shows an error, so a value that was invalid only
          // because of surrounding whitespace clears it; other fields keep their validation timing.
          setValue(field.id, trimmed, { shouldValidate: Boolean(errorMsg) });
        }
      }}
    />
  );
}

function splitRadioOptions<T>(options: T[], columnCount: number): T[][] {
  if (columnCount <= 1 || options.length === 0) {
    return [options];
  }

  const itemsPerColumn = Math.ceil(options.length / columnCount);
  const columns: T[][] = [];

  for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
    const startIndex = columnIndex * itemsPerColumn;
    const endIndex = startIndex + itemsPerColumn;
    const columnItems = options.slice(startIndex, endIndex);

    if (columnItems.length > 0) {
      columns.push(columnItems);
    }
  }

  return columns;
}

export default function FormField({ field, onFilesSelected, variant = 'default' }: FormFieldProps) {
  const label = field.label ?? '';
  const {
    register,
    unregister,
    control,
    getValues,
    setValue,
    formState: { errors, isSubmitted },
  } = useFormContext<Record<string, unknown>>();
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const selectedFilesRef = useRef<File[]>([]);

  if (field.type === 'hidden') {
    register(field.id);
    return null;
  }

  if (field.type === 'statement') {
    return <Text size="sm">{renderMarkdownLabel(label)}</Text>;
  }

  const allValues = useWatch({ control });
  const isVisible = !field.showIf || evaluateShowIf(field.showIf, allValues);
  const previousIsVisibleRef = useRef(isVisible);

  // For select/multiselect fields with optionsGroupedBy: watch the controlling
  // field's value so this field's options can be filtered to the matching group.
  const groupWatch = useWatch({ control, name: field.optionsGroupedBy ?? '__none__' });
  const previousGroupWatchRef = useRef(groupWatch);
  const isGroupDisabled = !!field.optionsGroupedBy && !groupWatch;
  
  // For combination therapy: watch the primary medication field to filter compatible options
  const compatibleWithWatch = useWatch({ control, name: field.filterCompatibleWith ?? '__none__' });
  const previousCompatibleWithWatchRef = useRef(compatibleWithWatch);
  
  // Filter options based on both optionsGroupedBy and compatibleWith
  let filteredOptions = field.options ?? [];
  
  // First apply group filtering if configured
  if (field.optionsGroupedBy && groupWatch) {
    filteredOptions = filteredOptions.filter((opt) => opt.group === groupWatch);
  }
  
  // Then apply compatibleWith filtering if configured
  if (field.filterCompatibleWith && compatibleWithWatch) {
    // Find the selected primary medication option to get its compatibleWith array
    const primaryMedicationOption = (field.options ?? []).find((opt) => opt.value === compatibleWithWatch);
    const compatibleValues = primaryMedicationOption?.compatibleWith;
    
    if (compatibleValues && compatibleValues.length > 0) {
      // Filter to only show medications in the compatibleWith array
      filteredOptions = filteredOptions.filter((opt) => compatibleValues.includes(opt.value));
    } else {
      // If compatibleWith is undefined or empty, show no options (medication can't be combined)
      filteredOptions = [];
    }
  }
  
  const groupedOptions = filteredOptions;
  const firstGroupedOptionValue = groupedOptions[0]?.value;
  const selectComponentKey = field.optionsGroupedBy || field.filterCompatibleWith
    ? `${field.id}-${String(groupWatch ?? '')}-${String(compatibleWithWatch ?? '')}`
    : field.id;

  useEffect(() => {
    if (!isVisible) {
      unregister(field.id);
    }

    if (!previousIsVisibleRef.current && isVisible && field.type === 'select') {
      const currentValue = getValues(field.id);

      if ((currentValue === undefined || currentValue === null || currentValue === '') && firstGroupedOptionValue) {
        setValue(field.id, firstGroupedOptionValue, { shouldDirty: true, shouldValidate: true });
      }
    }

    previousIsVisibleRef.current = isVisible;
  }, [field.id, field.type, firstGroupedOptionValue, getValues, isVisible, setValue, unregister]);

  // Reset this field's value whenever the controlling field's value changes, since
  // the previously selected option may no longer be in the filtered options list.
  // shouldValidate is intentionally omitted here so this reset doesn't immediately
  // flag the field as an error before the user has had a chance to pick an option.
  useEffect(() => {
    if (field.optionsGroupedBy && previousGroupWatchRef.current !== groupWatch) {
      const currentValue = getValues(field.id);
      const validOptionValues = new Set(groupedOptions.map((opt) => opt.value));

      if (field.type === 'multiselect') {
        const currentValues = Array.isArray(currentValue) ? currentValue.filter((value): value is string => typeof value === 'string') : [];
        const nextValues = currentValues.filter((value) => validOptionValues.has(value));

        if (nextValues.length !== currentValues.length) {
          setValue(field.id, nextValues, { shouldDirty: true });
        }
      } else {
        const currentStringValue = typeof currentValue === 'string' ? currentValue : '';

        if (currentStringValue !== '' && !validOptionValues.has(currentStringValue)) {
          setValue(field.id, '', { shouldDirty: true });
        }
      }
    }
    previousGroupWatchRef.current = groupWatch;
  }, [field.id, field.optionsGroupedBy, field.type, getValues, groupWatch, groupedOptions, setValue]);

  // Reset this field's value when the filterCompatibleWith field changes
  useEffect(() => {
    if (field.filterCompatibleWith && previousCompatibleWithWatchRef.current !== compatibleWithWatch) {
      const currentValue = getValues(field.id);
      const validOptionValues = new Set(groupedOptions.map((opt) => opt.value));

      if (field.type === 'multiselect') {
        const currentValues = Array.isArray(currentValue) ? currentValue.filter((value): value is string => typeof value === 'string') : [];
        const nextValues = currentValues.filter((value) => validOptionValues.has(value));

        if (nextValues.length !== currentValues.length) {
          setValue(field.id, nextValues, { shouldDirty: true });
        }
      } else {
        const currentStringValue = typeof currentValue === 'string' ? currentValue : '';

        if (currentStringValue !== '' && !validOptionValues.has(currentStringValue)) {
          setValue(field.id, '', { shouldDirty: true });
        }
      }
    }
    previousCompatibleWithWatchRef.current = compatibleWithWatch;
  }, [field.id, field.filterCompatibleWith, field.type, getValues, compatibleWithWatch, groupedOptions, setValue]);

  if (!isVisible) {
    return null;
  }

  const errorMsg = (errors[field.id]?.message ?? '') as string;
  const selectError = isSubmitted ? errorMsg || undefined : undefined;

  const rules = {
    required: field.required && !isGroupDisabled ? getRequiredMessage(field) : false,
    ...(field.validation?.maxLength != null
      ? { maxLength: { value: field.validation.maxLength, message: `Max ${field.validation.maxLength} characters` } }
      : {}),
    ...(field.validation?.pattern != null
      ? { pattern: { value: new RegExp(field.validation.pattern), message: field.validation.patternMessage ?? 'Invalid format' } }
      : {}),
    ...(field.type === 'date' && field.validation?.min != null
      ? {
          validate: (v: unknown) => {
            if (!v || typeof v !== 'string' || v === '') return true;
            return v >= field.validation!.min!
              ? true
              : (field.validation!.minMessage ?? `Must be on or after ${field.validation!.min}`);
          },
        }
      : {}),
    ...(field.validation?.dynamicMaxLength != null
      ? {
          validate: (v: unknown) => {
            if (!v || typeof v !== 'string' || v === '') return true;
            const effective = getEffectiveMaxLength(v, field.validation!.maxLength, field.validation!.dynamicMaxLength);
            return v.length <= effective ? true : `Max ${effective} characters`;
          },
        }
      : {}),
  };

  if (field.type === 'address') {
    return <AddressField field={field} />;
  }

  if (field.type === 'file') {
    const maxFiles = field.validation?.maxFiles ?? 3;
    const maxSizeBytes = field.validation?.maxSizeBytes ?? 5 * 1024 * 1024;
    const updateSelectedFiles = (nextFiles: File[], onChange: (value: number) => void) => {
      const acceptedFiles = onFilesSelected
        ? onFilesSelected(field.id, nextFiles.slice(0, maxFiles))
        : nextFiles.slice(0, maxFiles);
      selectedFilesRef.current = acceptedFiles;
      setSelectedFiles(acceptedFiles);
      onChange(acceptedFiles.length);
    };
    const fileRules = {
      validate: () => {
        const currentFiles = selectedFilesRef.current;
        if (field.required && currentFiles.length === 0) {
          return getRequiredMessage(field);
        }
        if (currentFiles.length > maxFiles) {
          return `Select no more than ${maxFiles} files`;
        }
        if (currentFiles.some((file) => file.size < 1 || file.size > maxSizeBytes)) {
          return `Each file must be no larger than ${Math.floor(maxSizeBytes / (1024 * 1024))} MB`;
        }
        return true;
      },
    };

    return (
      <Controller
        name={field.id}
        control={control}
        rules={fileRules}
        render={({ field: f }) => (
          <Stack gap="sm">
            <Input.Wrapper
              label={renderMarkdownLabel(label)}
              description={variant === 'default' && field.helpText ? renderMarkdownLabel(field.helpText) : undefined}
              required={field.required}
              error={errorMsg || undefined}
            >
              <Paper
                mt="xs"
                p="sm"
                withBorder
                className={variant === 'voucherDocument' ? 'voucher-upload-zone' : undefined}
                style={variant === 'default' ? { backgroundColor: '#f8f9fa' } : undefined}
              >
                {selectedFiles.length === 0 ? (
                  variant === 'voucherDocument' ? (
                    <Group gap="md" justify="space-between" wrap="wrap">
                      <FileButton
                        onChange={(nextFiles: File | File[] | null) => {
                          const newFiles = Array.isArray(nextFiles) ? nextFiles : nextFiles ? [nextFiles] : [];
                          const combined = [...selectedFiles, ...newFiles];
                          updateSelectedFiles(combined, f.onChange);
                        }}
                        multiple
                        disabled={false}
                      >
                        {(props) => (
                          <Button
                            {...props}
                            size="xs"
                            variant="subtle"
                            className="voucher-upload-action"
                            leftSection={<IconFileUpload size={17} />}
                          >
                            Upload file
                          </Button>
                        )}
                      </FileButton>
                      <Text size="sm" c="dimmed" fs="italic" className="voucher-upload-help">
                        {field.helpText ?? field.placeholder ?? 'Attach supporting documents'}
                      </Text>
                    </Group>
                  ) : (
                    <Stack gap="xs" align="center">
                      <IconFileUpload size={24} style={{ color: 'var(--mantine-color-dimmed)' }} />
                      <Text size="sm" c="dimmed" ta="center">
                        {field.placeholder ?? 'Attach call notes, screenshots, or supporting documents'}
                      </Text>
                      <FileButton
                        onChange={(nextFiles: File | File[] | null) => {
                          const newFiles = Array.isArray(nextFiles) ? nextFiles : nextFiles ? [nextFiles] : [];
                          const combined = [...selectedFiles, ...newFiles];
                          updateSelectedFiles(combined, f.onChange);
                        }}
                        multiple
                        disabled={false}
                      >
                        {(props) => (
                          <Button
                            {...props}
                            size="xs"
                            variant="light"
                            color="#000000"
                            leftSection={<IconPaperclip size={15} />}
                          >
                            Choose files
                          </Button>
                        )}
                      </FileButton>
                    </Stack>
                  )
                ) : (
                  <Text size="sm" c="dimmed">{selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected</Text>
                )}
              </Paper>
            </Input.Wrapper>
            {selectedFiles.length > 0 && (
              <Stack gap="xs">
                {selectedFiles.map((file, index) => {
                  const FileIcon = getFileIcon(file.name);
                  return (
                    <Group
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      justify="space-between"
                      gap="sm"
                      wrap="nowrap"
                      p="xs"
                      style={{ borderBottom: index === selectedFiles.length - 1 ? 'none' : '1px solid #e9ecef' }}
                    >
                      <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                        <FileIcon size={18} style={{ color: 'var(--mantine-color-dimmed)', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" truncate fw={500}>{file.name}</Text>
                          <Text size="xs" c="dimmed">{formatFileSize(file.size)}</Text>
                        </div>
                      </Group>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        aria-label={`Remove ${file.name}`}
                        title={`Remove ${file.name}`}
                        onClick={() => {
                          const updatedFiles = selectedFiles.filter((_, i) => i !== index);
                          updateSelectedFiles(updatedFiles, f.onChange);
                        }}
                      >
                        <IconX size={16} />
                      </ActionIcon>
                    </Group>
                  );
                })}
              </Stack>
            )}
            {selectedFiles.length > 0 && selectedFiles.length < maxFiles && (
              <FileButton
                onChange={(nextFiles: File | File[] | null) => {
                  const newFiles = Array.isArray(nextFiles) ? nextFiles : nextFiles ? [nextFiles] : [];
                  const combined = [...selectedFiles, ...newFiles];
                  updateSelectedFiles(combined, f.onChange);
                }}
                multiple
                disabled={false}
              >
                {(props) => (
                  <Button
                    {...props}
                    size="xs"
                    variant="light"
                    color="#000000"
                    leftSection={<IconPaperclip size={15} />}
                  >
                    Add more files
                  </Button>
                )}
              </FileButton>
            )}
          </Stack>
        )}
      />
    );
  }

  if (field.type === 'checkbox') {
    return (
      <Controller
        name={field.id}
        control={control}
        defaultValue={field.uncheckedValue ?? ''}
        rules={{
          validate: field.required
            ? (v) => v === field.checkedValue || 'This field must be checked'
            : undefined,
        }}
        render={({ field: f }) => (
          <Input.Wrapper
            label={field.showLabel === false ? undefined : renderMarkdownLabel(label)}
            description={field.helpText ? renderMarkdownLabel(field.helpText) : undefined}
            required={field.required}
            error={errorMsg || undefined}
          >
            <Checkbox
              mt="xs"
              label={field.checkboxLabel}
              checked={f.value === field.checkedValue}
              onChange={(e) =>
                f.onChange(
                  e.currentTarget.checked
                    ? (field.checkedValue ?? '')
                    : (field.uncheckedValue ?? '')
                )
              }
              color="var(--primary-color)"
            />
          </Input.Wrapper>
        )}
      />
    );
  }

  if (field.type === 'textarea') {
    return (
      <Textarea
        label={renderMarkdownLabel(label)}
        description={field.helpText ? renderMarkdownLabel(field.helpText) : undefined}
        placeholder={field.placeholder}
        required={field.required}
        error={errorMsg || undefined}
        autosize
        minRows={3}
        size="sm"
        radius="md"
        {...register(field.id, rules)}
      />
    );
  }

  if (field.type === 'radio') {
    return (
      <Controller
        name={field.id}
        control={control}
        rules={rules}
        render={({ field: f }) => {
          const options = field.options ?? [];
          const isBoxed = field.radioStyle === 'boxed';

          if (!isBoxed) {
            return (
              <Radio.Group
                label={renderMarkdownLabel(label)}
                description={field.helpText ? renderMarkdownLabel(field.helpText) : undefined}
                required={field.required}
                error={errorMsg || undefined}
                value={f.value as string ?? ''}
                onChange={f.onChange}
              >
                <Group mt="xs" gap="md">
                  {options.map((opt) => (
                    <Radio key={opt.value} value={opt.value} label={opt.label} color="var(--primary-color)" error={Boolean(errorMsg)} />
                  ))}
                </Group>
              </Radio.Group>
            );
          }

          const useTwoColumns = !isMobile && options.length >= 4;
          const optionColumns = splitRadioOptions(options, useTwoColumns ? 2 : 1);

          return (
            <Radio.Group
              label={renderMarkdownLabel(label)}
              description={field.helpText ? renderMarkdownLabel(field.helpText) : undefined}
              required={field.required}
              error={errorMsg || undefined}
              value={f.value as string ?? ''}
              onChange={f.onChange}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: useTwoColumns ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                  gap: 'var(--mantine-spacing-sm)',
                  marginTop: 'var(--mantine-spacing-xs)',
                }}
              >
                {optionColumns.map((columnOptions, columnIndex) => (
                  <div
                    key={`radio-column-${columnIndex}`}
                    style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mantine-spacing-sm)' }}
                  >
                    {columnOptions.map((opt) => {
                      const isSelected = f.value === opt.value;

                      return (
                        <div
                          key={opt.value}
                          onClick={() => f.onChange(opt.value)}
                          style={{
                            alignItems: 'center',
                            backgroundColor: isSelected ? 'rgba(0, 0, 0, 0.02)' : 'white',
                            border: `1px solid ${isSelected ? 'var(--primary-color)' : errorMsg ? 'var(--mantine-color-error)' : 'var(--mantine-color-gray-3)'}`,
                            borderRadius: 'var(--mantine-radius-md)',
                            cursor: 'pointer',
                            display: 'flex',
                            minHeight: '48px',
                            padding: '12px 14px',
                          }}
                        >
                          <Radio
                            value={opt.value}
                            label={opt.label}
                            color="var(--primary-color)"
                            error={Boolean(errorMsg)}
                            styles={{
                              body: { flex: 1 },
                              root: { width: '100%' },
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </Radio.Group>
          );
        }}
      />
    );
  }

  if (field.type === 'select') {
    // Mantine's data prop reserves the "group" key for its own native option-grouping
    // format ({ group, items }), so strip our schema's "group" tag before passing
    // options through — otherwise Mantine misreads our items as that format and crashes.
    const selectOptions = groupedOptions.map(({ value, label }) => ({ value, label }));
    // field.placeholder doubles as the "pick the controlling field first" hint while
    // disabled; once enabled, show a normal placeholder instead so it doesn't keep
    // telling the user to do something they've already done.
    const selectPlaceholder = isGroupDisabled ? field.placeholder : field.optionsGroupedBy ? `Select ${label}` : field.placeholder;

    return (
      <Controller
        name={field.id}
        control={control}
        rules={rules}
        render={({ field: f }) => (
          <Select
            key={selectComponentKey}
            label={renderMarkdownLabel(label)}
            description={field.helpText ? renderMarkdownLabel(field.helpText) : undefined}
            placeholder={selectPlaceholder}
            withAsterisk={field.required}
            disabled={isGroupDisabled}
            error={selectError}
            data={selectOptions}
            value={typeof f.value === 'string' && f.value !== '' ? f.value : null}
            onChange={f.onChange}
            searchable
            clearable
            size="sm"
            radius="md"
          />
        )}
      />
    );
  }

  if (field.type === 'multiselect') {
    // See the corresponding comment in the 'select' branch above: strip "group"
    // before passing options to Mantine's data prop.
    const multiSelectOptions = groupedOptions.map(({ value, label }) => ({ value, label }));
    // See the corresponding comment in the 'select' branch above for why this switches.
    const multiSelectPlaceholder = isGroupDisabled ? field.placeholder : field.optionsGroupedBy ? `Select ${label}` : field.placeholder;

    return (
      <Controller
        name={field.id}
        control={control}
        rules={rules}
        render={({ field: f }) => (
          <MultiSelect
            label={renderMarkdownLabel(label)}
            description={field.helpText ? renderMarkdownLabel(field.helpText) : undefined}
            placeholder={multiSelectPlaceholder}
            withAsterisk={field.required}
            disabled={isGroupDisabled}
            error={selectError}
            data={multiSelectOptions}
            value={f.value as string[] ?? []}
            onChange={f.onChange}
            searchable
            clearable
            size="sm"
            radius="md"
          />
        )}
      />
    );
  }

  // Default: text / tel / date
  const inputType = field.type === 'tel' ? 'tel' : field.type === 'date' ? 'date' : 'text';
  const registration = register(field.id, rules);
  const hasDynamic = field.type !== 'date' && field.validation?.dynamicMaxLength != null;

  return (
    <>
      {hasDynamic ? (
        <DynamicMaxLengthInput
          field={field}
          label={label}
          inputType={inputType}
          errorMsg={errorMsg}
          registration={registration}
          setValue={setValue}
        />
      ) : (
        <TextInput
          label={renderMarkdownLabel(label)}
          description={field.helpText ? renderMarkdownLabel(field.helpText) : undefined}
          placeholder={field.type !== 'date' ? field.placeholder : undefined}
          required={field.required}
          type={inputType}
          error={errorMsg || undefined}
          size="sm"
          radius="md"
          maxLength={field.type !== 'date' ? field.validation?.maxLength : undefined}
          {...(field.type === 'date' && field.validation?.min ? { min: field.validation.min } : {})}
          {...registration}
          onBlur={(e) => {
            registration.onBlur(e);
            if (field.type !== 'date') {
              const trimmed = e.target.value.trim();
              if (trimmed !== e.target.value) {
                // See the matching comment in DynamicMaxLengthInput.
                setValue(field.id, trimmed, { shouldValidate: Boolean(errorMsg) });
              }
            }
          }}
        />
      )}
      {field.type !== 'date' && field.validation?.maxLength != null && (
        <CharCounter
          fieldId={field.id}
          maxLength={field.validation.maxLength}
          dynamicMaxLength={field.validation.dynamicMaxLength}
        />
      )}
      {field.type === 'date' && (
        <Text size="xs" c="dimmed" mt={4}>
          Enter date as MM/DD/YYYY
        </Text>
      )}
    </>
  );
}
